import { useState, useCallback } from 'react';
import { ModalType } from '../components/ConfirmationModal';

interface ModalConfig {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: ModalType;
  showCancel?: boolean;
  isRtl?: boolean;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
}

interface UseConfirmationModalReturn {
  isOpen: boolean;
  modalConfig: ModalConfig | null;
  showModal: (config: ModalConfig) => void;
  hideModal: () => void;
  confirm: () => Promise<void>;
  cancel: () => void;
}

export const useConfirmationModal = (): UseConfirmationModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);

  const showModal = useCallback((config: ModalConfig) => {
    setModalConfig(config);
    setIsOpen(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsOpen(false);
    setModalConfig(null);
  }, []);

  const confirm = useCallback(async () => {
    if (modalConfig?.onConfirm) {
      await modalConfig.onConfirm();
    }
    hideModal();
  }, [modalConfig, hideModal]);

  const cancel = useCallback(() => {
    if (modalConfig?.onCancel) {
      modalConfig.onCancel();
    }
    hideModal();
  }, [modalConfig, hideModal]);

  return {
    isOpen,
    modalConfig,
    showModal,
    hideModal,
    confirm,
    cancel
  };
};

// Predefined modal configurations for common use cases
export const modalPresets = {
  delete: (itemName: string, onConfirm: () => void | Promise<void>): ModalConfig => ({
    title: 'حذف آیتم',
    message: `آیا مطمئن هستید که می‌خواهید ${itemName} را حذف کنید؟`,
    confirmText: 'حذف',
    cancelText: 'لغو',
    type: 'error' as ModalType,
    isRtl: true,
    onConfirm
  }),

  deleteEn: (itemName: string, onConfirm: () => void | Promise<void>): ModalConfig => ({
    title: 'Delete Item',
    message: `Are you sure you want to delete ${itemName}?`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    type: 'error' as ModalType,
    isRtl: false,
    onConfirm
  }),

  logout: (onConfirm: () => void | Promise<void>): ModalConfig => ({
    title: 'خروج',
    message: 'آیا مطمئن هستید که می‌خواهید از حساب خود خارج شوید؟',
    confirmText: 'خروج',
    cancelText: 'لغو',
    type: 'warning' as ModalType,
    isRtl: true,
    onConfirm
  }),

  success: (message: string, onConfirm?: () => void): ModalConfig => ({
    title: 'موفقیت',
    message,
    confirmText: 'تایید',
    type: 'success' as ModalType,
    showCancel: false,
    isRtl: true,
    onConfirm
  }),

  error: (message: string, onConfirm?: () => void): ModalConfig => ({
    title: 'خطا',
    message,
    confirmText: 'تایید',
    type: 'error' as ModalType,
    showCancel: false,
    isRtl: true,
    onConfirm
  }),

  info: (message: string, onConfirm?: () => void): ModalConfig => ({
    title: 'اطلاعات',
    message,
    confirmText: 'تایید',
    type: 'info' as ModalType,
    showCancel: false,
    isRtl: true,
    onConfirm
  }),

  warning: (message: string, onConfirm?: () => void): ModalConfig => ({
    title: 'هشدار',
    message,
    confirmText: 'تایید',
    type: 'warning' as ModalType,
    showCancel: false,
    isRtl: true,
    onConfirm
  }),

  // English versions
  successEn: (message: string, onConfirm?: () => void): ModalConfig => ({
    title: 'Success',
    message,
    confirmText: 'OK',
    type: 'success' as ModalType,
    showCancel: false,
    isRtl: false,
    onConfirm
  }),

  errorEn: (message: string, onConfirm?: () => void): ModalConfig => ({
    title: 'Error',
    message,
    confirmText: 'OK',
    type: 'error' as ModalType,
    showCancel: false,
    isRtl: false,
    onConfirm
  }),

  // Custom Persian message like in your image
  customPersian: (message: string, onConfirm?: () => void): ModalConfig => ({
    message,
    confirmText: 'تایید',
    cancelText: 'لغو',
    type: 'info' as ModalType,
    isRtl: true,
    onConfirm
  })
}; 